import check from '../libraries/check-types-ext';
import ProductCatalog from './ProductCatalog';
import * as _ from 'lodash';
import AppError from '../libraries/AppError';
import mapProductOverride from './ProductOverrideMapper';
import getLuxonImpl from '../plugins/commonLibraries/timezone/timeboxImpl';
import { copyObject } from '../libraries/Utils';
import { ApmHelper, ApmSpanType } from '../libraries/ApmHelper';
import { dateRuleTypesEnum } from '../types/EnumTypes';
import OrderHelperObject from '../orders/OrderHelperObject';
import { shouldRuleBeAppliedToShop } from '../priceRules/priceRulesHelpers';
export const ProductMgr = Object.freeze({
    getFullPrice: (product) => {
        let fullProductPrice = product.price;
        if (product.groups) {
            for (const i in product.groups) {
                const group = product.groups[i];
                if (!group.options.useInGroupOnly) {
                    for (const j in group.products) {
                        const prod = group.products[j];
                        fullProductPrice += prod.options.amount > 0 ? prod.price || 0 : 0;
                    }
                }
            }
        }
        return fullProductPrice;
    },
    getProductName: (product, productName) => {
        check.object.orFail(product);
        if (product.configurations) {
            const optionalNames = [];
            for (const i in product.configurations) {
                const config = product.configurations[i];
                const productOptions = config.options.options;
                if (productOptions) {
                    if (config.appearance === 'radiobuttons' || config.appearance === 'switches') {
                        const selectedOptions = productOptions.filter(function (o) {
                            return o.id === config.selected;
                        });
                        const option = selectedOptions.length > 0 ? selectedOptions[0] : undefined;
                        if (option) {
                            optionalNames.push(config.name + ': ' + option.name);
                        }
                    }
                }
                if (config.appearance === 'textarea') {
                    optionalNames.push(config.name + ": '" + (config.value && config.value !== 'undefined' ? config.value : '') + "'");
                }
            }
            if (optionalNames.length > 0) {
                productName += ' - ' + optionalNames.join(', ') + '';
            }
        }
        return productName;
    },
    _createEnrichedProductCatalogData(productCatalogData, priceRules, selectedShopId, pickupTimeZone, userTimeZone, currentDate, opts = {}) {
        console.warn('ERROR - THIS FUNCTION SHOULD NOT BE USED ON FRONTEND!');
        const { tags = [], isAdmin, orderPickupDate = undefined } = opts;
        if (!Array.isArray(priceRules)) {
            throw AppError.CodingError('Expected an array').addData('priceRules', priceRules);
        }
        const filteredByShop = priceRules.filter((rule) => {
            if (!rule) {
                throw AppError.CodingError('Somehow an undefined filter was passed in here');
            }
            if (selectedShopId === null) {
                return true;
            }
            return shouldRuleBeAppliedToShop(rule.shopPattern, selectedShopId);
        });
        const filteredRules = ApmHelper.apmDecorator(() => {
            return filteredByShop.filter((rule) => {
                const { Formats: timeBoxFormats, timebox: timeBox } = getLuxonImpl();
                if (Array.isArray(rule.orderingDatePattern)) {
                    if (rule.dateRule !== dateRuleTypesEnum.pickupDate) {
                        const shopCurrentDate = timeBox
                            .convertRelativeStringDate(currentDate, userTimeZone, pickupTimeZone)
                            .format(timeBoxFormats.YYYY_MM_DD)
                            .exec();
                        const limitDates = _.uniq(rule.orderingDatePattern.reduce((carry, dateFilterItem) => {
                            return [...carry, ...OrderHelperObject.parseWildcardDates(dateFilterItem, pickupTimeZone)];
                        }, []));
                        return limitDates.includes(shopCurrentDate);
                    }
                    if (rule.dateRule === dateRuleTypesEnum.pickupDate) {
                        if (rule.orderingDatePattern.length === 2 && orderPickupDate) {
                            const firstDate = timeBox
                                .fromJsObject(new Date(rule.orderingDatePattern[0]))
                                .format(timeBoxFormats.YYYY_MM_DD)
                                .exec();
                            const limitByPickupDates = [firstDate];
                            const db = new Date(rule.orderingDatePattern[0]);
                            const de = new Date(rule.orderingDatePattern[1]);
                            while (db < de) {
                                db.setDate(db.getDate() + 1);
                                const nextDate = timeBox.fromJsObject(db).format(timeBoxFormats.YYYY_MM_DD).exec();
                                limitByPickupDates.push(nextDate);
                            }
                            return limitByPickupDates.includes(orderPickupDate);
                        }
                    }
                }
                else if (Array.isArray(rule.weekDay)) {
                    const weekDay = timeBox.realNow().weekday().exec();
                    return rule.weekDay.includes(weekDay);
                }
                else {
                    return false;
                }
            });
        }, {
            apmLabel: 'ProductMgrCreateEnrichedProductCatalog_filteredRules',
            apmType: ApmSpanType.PURE_FUNCTION,
        })();
        const getApplicableRules = (productId) => {
            const compare = (rule) => {
                return rule.dateRule === dateRuleTypesEnum.creationDate &&
                    (!rule.voucherPattern || rule.voucherPattern.length === 0)
                    ? -1
                    : rule.dateRule === dateRuleTypesEnum.paymentDate
                        ? -1
                        : 1;
            };
            const sortedRules = filteredRules.sort(compare);
            return sortedRules
                .filter((rule) => rule.productPattern && rule.productPattern.includes(productId))
                .map((rule) => {
                return {
                    discountId: rule.id,
                    discountValue: parseFloat(rule.discountValue),
                    discountType: rule.discountType,
                    voucherPattern: rule.voucherPattern,
                    options: rule.options,
                    productPattern: rule.productPattern,
                };
            });
        };
        const productsArray = ApmHelper.apmDecorator(() => {
            return productCatalogData.products.map((product) => {
                const newProduct = mapProductOverride(copyObject(product), {
                    activeTags: tags,
                    shopId: selectedShopId,
                    isAdmin: isAdmin,
                });
                const applicableRules = getApplicableRules(product.id);
                newProduct.applicableDiscounts = applicableRules;
                if (applicableRules.length) {
                    newProduct.discount = applicableRules[0];
                    const isEmptyArrayOrNull = (testArray) => {
                        if (Array.isArray(testArray) && testArray.length > 0) {
                            return false;
                        }
                        return true;
                    };
                    const dealTagApplicableVouchers = (() => {
                        return isEmptyArrayOrNull(applicableRules[0].voucherPattern);
                    })();
                    const dealTagApplicableGameGifts = (() => {
                        if (applicableRules[0].options &&
                            (applicableRules[0].options.giftDiscount === undefined ||
                                applicableRules[0].options.giftDiscount === null)) {
                            return true;
                        }
                        return false;
                    })();
                    if (dealTagApplicableVouchers && dealTagApplicableGameGifts) {
                        if (Array.isArray(newProduct.tags)) {
                            newProduct.tags.push('deal');
                        }
                        else {
                            newProduct.tags = ['deal'];
                        }
                    }
                }
                return newProduct;
            });
        }, {
            apmLabel: 'ProductMgrCreateEnrichedProductCatalog_productsArray',
            apmType: ApmSpanType.PURE_FUNCTION,
        })();
        const categories = ApmHelper.apmDecorator(() => {
            return productCatalogData.categories.map((existingCategory) => {
                const category = copyObject(existingCategory);
                if (Array.isArray(category.fillWithProductsFromTags)) {
                    const taggedProducts = productsArray
                        .filter((product) => Array.isArray(product.tags))
                        .filter((product) => product.tags.some((tag) => category.fillWithProductsFromTags.includes(tag)))
                        .map((product) => product.id);
                    if (taggedProducts.length > 0) {
                        return Object.assign(Object.assign({}, category), { elements: [
                                {
                                    props: {
                                        products: taggedProducts,
                                    },
                                    objType: 'ProductCatalogList',
                                },
                            ] });
                    }
                }
                return category;
            });
        }, {
            apmLabel: 'ProductMgrCreateEnrichedProductCatalog_categories',
            apmType: ApmSpanType.PURE_FUNCTION,
        })();
        return {
            categories,
            products: productsArray,
            departments: productCatalogData.departments,
            priceRules,
            selectedShopId,
            pickupTimeZone,
            userTimeZone,
            currentDate,
        };
    },
    createEnrichedProductCatalog: ApmHelper.apmDecorator((productCatalogData, priceRules, selectedShopId, pickupTimeZone, userTimeZone, currentDate, opts = {}) => {
        const enrichedProductCatalogData = ProductMgr._createEnrichedProductCatalogData(productCatalogData, priceRules, selectedShopId, pickupTimeZone, userTimeZone, currentDate, opts);
        const modifiedEnrichedProductCatalogData = (opts.productCatalogHooks || []).reduce((carry, hook) => {
            return hook(carry);
        }, enrichedProductCatalogData);
        return new ProductCatalog(modifiedEnrichedProductCatalogData);
    }, {
        apmLabel: 'ProductMgrCreateEnrichedProductCatalog',
        apmType: ApmSpanType.PURE_FUNCTION,
    }),
    getPossibleProductUnits: () => {
        return [
            'stk.',
            '2 stk.',
            '3 stk.',
            '4 stk.',
            'kuvert',
            '100 g.',
            'net',
            'menu',
            'pose',
            'billet',
            'skiver',
            'blade',
            'gram',
            'pynt',
            'båd',
            'kg.',
            'fustage',
            '½ kg',
            'kasse',
            'pakke',
        ];
    },
});
